import Modal from 'lib/src/components/modal/Modal';
import Form from 'lib/src/components/form/Form';
import FormRow from 'lib/src/components/form/FormRow';
import TextInput from 'lib/src/components/form/TextInput';
import Checkbox from 'lib/src/components/form/Checkbox';
import FormSubTitle from 'lib/src/components/form/FormSubTitle';
import DatePicker from 'lib/src/components/form/DatePicker';
import dayjs from 'dayjs';
import useUpdateStoreException from './hooks/useUpdateStoreException';
import { BusinessHoursException } from '@customTypes/shared/Store';
import FormField from 'lib/src/components/form/FormField';

const UpdateStoreExceptionModal: React.FC<UpdateCustomerProps> = ({ exception, storeID }) => {
    const {
        formState: {
            name,
            startOn,
            endOn,
            openTime,
            closeTime,
            isClosed,
            isRecurring,
            isDateRange,
        },
        handleChange,
        handleSubmit,
        closeModal,
        isPosting,
    } = useUpdateStoreException(exception, storeID);

    return (
        <Modal closeModal={closeModal} title="Update Store Exception">
            <Form onSubmit={handleSubmit} isPosting={isPosting} onCancel={closeModal}>
                <FormSubTitle>Exception Details</FormSubTitle>
                <FormRow>
                    <TextInput
                        name="name"
                        value={name}
                        onChange={handleChange}
                        label="Name"
                        placeholder="Name"
                        required
                    />
                </FormRow>

                <FormRow>
                    <div className="flex-6">
                        <Checkbox
                            name="isDateRange"
                            label="Include date range?"
                            value={isDateRange}
                            onChange={handleChange}
                            disabled={isRecurring}
                        />
                    </div>

                    <div className="flex-6">
                        <Checkbox
                            name="isRecurring"
                            value={isRecurring}
                            onChange={handleChange}
                            label="Recurring"
                        />
                    </div>
                </FormRow>

                <FormRow>
                    <FormField name="" className="no-margin">
                        <DatePicker
                            name="startOn"
                            value={startOn}
                            onChange={handleChange}
                            label="Start On"
                            required
                        />
                    </FormField>

                    <FormField name="" className="no-margin">
                        {isDateRange && !isRecurring && (
                            <DatePicker
                                name="endOn"
                                value={endOn}
                                onChange={handleChange}
                                label="End On"
                                customValidate={value =>
                                    value && !isRecurring && !dayjs(value).isAfter(startOn)
                                        ? 'End date must be after start date'
                                        : null
                                }
                                disabled={isRecurring}
                                required
                            />
                        )}
                    </FormField>
                </FormRow>

                <FormRow>
                    <Checkbox
                        name="isClosed"
                        value={isClosed}
                        onChange={handleChange}
                        label="Closed"
                    />
                </FormRow>

                {!isClosed && (
                    <FormRow>
                        <TextInput
                            type="time"
                            name="openTime"
                            value={isClosed ? '' : openTime ?? ''}
                            onChange={handleChange}
                            label="Open Time"
                            placeholder="Open Time"
                            required={!isClosed}
                            disabled={isClosed}
                        />
                        <TextInput
                            type="time"
                            name="closeTime"
                            value={isClosed ? '' : closeTime ?? ''}
                            onChange={handleChange}
                            label="Close Time"
                            placeholder="Close Time"
                            required={!isClosed}
                            customValidate={value =>
                                !isClosed &&
                                !dayjs(`0000-00-00T${value}`).isAfter(`0000-00-00T${openTime}`)
                                    ? 'Close time must be after open time'
                                    : null
                            }
                            disabled={isClosed}
                        />
                    </FormRow>
                )}
            </Form>
        </Modal>
    );
};

interface UpdateCustomerProps {
    exception: BusinessHoursException;
    storeID: number;
}

export default UpdateStoreExceptionModal;
